import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Button, Alert, CircularProgress,
    ThemeProvider, CssBaseline, AppBar, Toolbar, IconButton,
    Avatar, Menu, MenuItem, Divider, ListItemIcon, ListItemText
} from '@mui/material';
import {
    Person, ExitToApp, AccountCircle, Login, Security,
    Business, CheckCircle
} from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';

interface AuthWrapperProps {
    children: React.ReactNode;
}

interface UserInfo {
    userDetails: string;
    identityProvider: string;
    userId: string;
    userRoles: string[];
}

// Create a consistent dark theme matching the main app
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#90caf9', // Light blue for primary elements
        },
        secondary: {
            main: 'rgb(0,165,163)', // Teal accent color
        },
        background: {
            default: '#121212', // Very dark background
            paper: '#1e1e1e', // Slightly lighter dark for paper surfaces
        },
        text: {
            primary: '#ffffff', // White text
            secondary: '#b0bec5', // Lighter gray text
        },
        success: {
            main: '#4caf50',
        }
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h4: {
            fontWeight: 600,
            color: 'rgb(0,165,163)',
        },
        h6: {
            fontWeight: 600,
        },
        body1: {
            fontSize: '1rem',
        },
        body2: {
            fontSize: '0.875rem',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                containedPrimary: {
                    backgroundColor: 'rgb(0,165,163)',
                    color: '#ffffff',
                    fontWeight: 600,
                    borderRadius: '8px',
                    textTransform: 'none',
                    padding: '12px 32px',
                    fontSize: '1rem',
                    '&:hover': {
                        backgroundColor: 'rgb(0,145,143)',
                        boxShadow: '0 4px 12px rgba(0,165,163,0.3)',
                    },
                },
                outlined: {
                    borderColor: '#90caf9',
                    color: '#90caf9',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                        backgroundColor: 'rgba(144, 202, 249, 0.08)',
                        borderColor: '#64b5f6',
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                },
                standardInfo: {
                    backgroundColor: 'rgba(144, 202, 249, 0.1)',
                    color: '#90caf9',
                    '& .MuiAlert-icon': {
                        color: '#90caf9',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    borderRadius: '12px',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#1e1e1e',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                },
            },
        },
    },
});

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const location = useLocation();

    // Define routes that should be accessible without authentication
    const anonymousRoutes: string[] = [];
    const isAnonymousRoute = anonymousRoutes.some(route => location.pathname.startsWith(route));

    useEffect(() => {
        // Skip authentication check for anonymous routes
        if (isAnonymousRoute) {
            setLoading(false);
            setIsAuthenticated(false);
            return;
        }

        checkAuthStatus();
    }, [isAnonymousRoute]);

    const checkAuthStatus = async () => {
        try {
            setLoading(true);
            setError(null);

            // Check if user is authenticated by calling the auth endpoint
            const response = await fetch('/.auth/me');

            if (response.ok) {
                const authData = await response.json();

                // Check if we have authentication data
                if (authData && authData.clientPrincipal) {
                    const user: UserInfo = {
                        userDetails: authData.clientPrincipal.userDetails,
                        identityProvider: authData.clientPrincipal.identityProvider,
                        userId: authData.clientPrincipal.userId,
                        userRoles: authData.clientPrincipal.userRoles || []
                    };

                    setUserInfo(user);
                    setIsAuthenticated(true);
                    console.log('✅ User authenticated:', user);
                } else {
                    // User not authenticated - redirect to login
                    setIsAuthenticated(false);
                    console.log('❌ User not authenticated - clientPrincipal is null, redirecting to login');
                    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + window.location.pathname;
                    return;
                }
            } else if (response.status === 401) {
                // This is expected when not authenticated - redirect to login
                setIsAuthenticated(false);
                console.log('❌ User not authenticated - 401 response, redirecting to login');
                window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + window.location.pathname;
                return;
            } else {
                throw new Error(`Auth check failed: ${response.status}`);
            }
        } catch (err) {
            console.error('Error checking auth status:', err);
            // Don't show error for auth check, just treat as not authenticated
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = () => {
        // Redirect to Azure AD login
        window.location.href = '/.auth/login/aad';
    };

    const handleLogout = () => {
        // Redirect to logout - ensure we go to a page that will redirect back to root
        window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Get user display name and email
    const getUserDisplayInfo = () => {
        if (!userInfo?.userDetails) return { name: 'User', email: '', initials: 'U' };

        const email = userInfo.userDetails;
        const name = email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

        return { name, email, initials };
    };

    if (loading) {
        return (
            <ThemeProvider theme={darkTheme}>
                <CssBaseline />
                <Box sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #121212 0%, #1e1e1e 100%)',
                }}>
                    <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
                        <Box sx={{ mb: 4 }}>
                            <img src={logo} alt="IOD PARC Logo" style={{ height: '64px', marginBottom: '24px' }} />
                            <Typography variant="h4" gutterBottom sx={{ color: 'rgb(0,165,163)', fontWeight: 600 }}>
                                DocHub
                            </Typography>
                        </Box>
                        <CircularProgress size={40} sx={{ color: 'rgb(0,165,163)' }} />
                        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                            Checking authentication...
                        </Typography>
                    </Container>
                </Box>
            </ThemeProvider>
        );
    }

    // For anonymous routes, render children directly without authentication
    if (isAnonymousRoute) {
        return (
            <ThemeProvider theme={darkTheme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        );
    }

    if (!isAuthenticated) {
        return (
            <ThemeProvider theme={darkTheme}>
                <CssBaseline />
                <Box sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%, #0a0a0a 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 20% 80%, rgba(0,165,163,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0,185,183,0.1) 0%, transparent 50%)',
                        pointerEvents: 'none',
                    }
                }}>
                    <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                        <Box sx={{
                            textAlign: 'center',
                            p: { xs: 3, sm: 4, md: 6 },
                            backgroundColor: 'rgba(30, 30, 30, 0.95)',
                            borderRadius: 4,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 8px 32px rgba(0,165,163,0.1)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            backdropFilter: 'blur(20px)',
                            maxWidth: '600px',
                            mx: 'auto',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, rgb(0,165,163) 0%, rgb(0,185,183) 50%, rgb(0,165,163) 100%)',
                                borderRadius: '4px 4px 0 0',
                            }
                        }}>
                            {/* Logo and Title Section - Enhanced */}
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{
                                    mb: 2,
                                    p: 1.5,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(0,165,163,0.1)',
                                    border: '2px solid rgba(0,165,163,0.3)',
                                    width: 'fit-content',
                                    mx: 'auto'
                                }}>
                                    <img
                                        src={logo}
                                        alt="IOD PARC Logo"
                                        style={{
                                            height: '64px',
                                            width: '64px',
                                            filter: 'drop-shadow(0 4px 8px rgba(0,165,163,0.3))'
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant="h3"
                                    gutterBottom
                                    sx={{
                                        color: 'rgb(0,165,163)',
                                        fontWeight: 700,
                                        fontSize: { xs: '2.2rem', sm: '2.8rem' },
                                        textShadow: '0 2px 8px rgba(0,165,163,0.3)',
                                        letterSpacing: '1px',
                                        mb: 1
                                    }}
                                >
                                    DocHub
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 400,
                                        fontSize: { xs: '1rem', sm: '1.2rem' },
                                        mb: 1.5
                                    }}
                                >
                                    Document Search & Analysis Platform
                                </Typography>
                                <Box sx={{
                                    width: '80px',
                                    height: '3px',
                                    background: 'linear-gradient(90deg, rgb(0,165,163), rgb(0,185,183))',
                                    mx: 'auto',
                                    borderRadius: '2px'
                                }} />
                            </Box>

                            {/* Welcome Message - Enhanced */}
                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    variant="h5"
                                    color="text.primary"
                                    paragraph
                                    sx={{
                                        fontSize: { xs: '1.1rem', sm: '1.3rem' },
                                        fontWeight: 500,
                                        mb: 2
                                    }}
                                >
                                    Welcome to IOD PARC DocHub
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    paragraph
                                    sx={{
                                        fontSize: '1rem',
                                        lineHeight: 1.5,
                                        maxWidth: '480px',
                                        mx: 'auto'
                                    }}
                                >
                                    Please sign in with your Office 365 account to access our comprehensive suite of
                                    document analysis tools including CV search, proposal analysis, CV converter, and interactive document chat features.
                                </Typography>
                            </Box>

                            {/* Security Notice - Enhanced */}
                            <Alert
                                severity="info"
                                sx={{
                                    mb: 3,
                                    textAlign: 'left',
                                    backgroundColor: 'rgba(144, 202, 249, 0.08)',
                                    border: '1px solid rgba(144, 202, 249, 0.2)',
                                    borderRadius: 2,
                                    '& .MuiAlert-icon': {
                                        fontSize: '1.3rem'
                                    }
                                }}
                                icon={<Security sx={{ color: '#90caf9' }} />}
                            >
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                    Secure Authentication Required
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    This application uses Microsoft Azure Active Directory for enterprise-grade
                                    security, ensuring your data remains protected with industry-standard authentication protocols.
                                </Typography>
                            </Alert>

                            {/* Sign In Button - Enhanced */}
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Login />}
                                onClick={handleLogin}
                                fullWidth
                                sx={{
                                    py: 2.5,
                                    px: 6,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    background: 'linear-gradient(45deg, rgb(0,165,163) 30%, rgb(0,185,183) 90%)',
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    boxShadow: '0 8px 32px rgba(0,165,163,0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, rgb(0,145,143) 30%, rgb(0,165,163) 90%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 40px rgba(0,165,163,0.4)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    }
                                }}
                            >
                                Sign In with Office 365
                            </Button>

                            {error && (
                                <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {/* Footer - Enhanced */}
                            <Box sx={{
                                mt: 4,
                                pt: 3,
                                borderTop: '1px solid rgba(255,255,255,0.08)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5
                            }}>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        textAlign: 'center',
                                        fontSize: '0.95rem',
                                        fontWeight: 400,
                                        letterSpacing: '0.5px',
                                        lineHeight: 1.4
                                    }}
                                >
                                    IOD PARC © {new Date().getFullYear()}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{
                                        textAlign: 'center',
                                        fontSize: '0.75rem',
                                        opacity: 0.7
                                    }}
                                >
                                    Powered by Azure Cloud Infrastructure
                                </Typography>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </ThemeProvider>
        );
    }

    const { name, email, initials } = getUserDisplayInfo();

    // User is authenticated, render the app with a sleek user menu in the top right
    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            {/* User Menu - Positioned to not interfere with database status */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 8,
                    right: 8,
                    zIndex: 1600, // Higher than AppBar
                }}
            >
                <IconButton
                    onClick={handleMenuClick}
                    sx={{
                        p: 1,
                        backgroundColor: 'rgba(30, 30, 30, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 2,
                        '&:hover': {
                            backgroundColor: 'rgba(30, 30, 30, 0.95)',
                            borderColor: 'rgba(144, 202, 249, 0.3)',
                        },
                    }}
                >
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: 'rgb(0,165,163)',
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                        }}
                    >
                        {initials}
                    </Avatar>
                </IconButton>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'background.paper',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 2,
                            mt: 1,
                            minWidth: 240,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    {/* User Info Header */}
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: 'rgb(0,165,163)',
                                    color: 'white',
                                    fontWeight: 600,
                                }}
                            >
                                {initials}
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {email}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Status Info */}
                    <MenuItem disabled sx={{ py: 1 }}>
                        <ListItemIcon>
                            <CheckCircle fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography variant="body2" color="success.main">
                                Authenticated via {userInfo?.identityProvider}
                            </Typography>
                        </ListItemText>
                    </MenuItem>

                    <Divider sx={{ my: 0.5 }} />

                    {/* Sign Out */}
                    <MenuItem
                        onClick={handleLogout}
                        sx={{
                            py: 1.5,
                            '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            },
                        }}
                    >
                        <ListItemIcon>
                            <ExitToApp fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography variant="body2" color="error">
                                Sign Out
                            </Typography>
                        </ListItemText>
                    </MenuItem>
                </Menu>
            </Box>
            {children}
        </ThemeProvider>
    );
};

export default AuthWrapper; 